import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import ExportModal from "~/components/Document/exportModal";
import Button from "~/components/ui/button";
import { TOAST_MESSAGES } from "~/constants/messages";
import EstimationDescription from "~/features/estimation/estimationDescription";
import EstimationTable from "~/features/estimation/estimationTable";
import { DocumentApi } from "~/functions/api/document";
import type { Document } from "~/interface/document";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

interface UseCaseRow {
  selected?: boolean;
  [key: string]: string | boolean | undefined;
}

interface UseCaseTable {
  headers?: string[];
  rows?: (string[] | UseCaseRow)[];
}

interface EstimationDocumentContent {
  usecase_table?: UseCaseTable;
  [key: string]: any;
}

// Mock data types
interface IEstimationItem {
  selected: boolean;
  useCase: string;
  featureCategory: string;
  implementationMethod: string;
  workload: string;
  complexity?: string;
}

export interface IEstimationDescription {
  useCase: string;
  description: {
    details: string;
    reason: string;
  };
}

const EstimationPage = () => {
  const { currentUser, currentProject, organization } = useSelector(
    (state: RootState) => state.auth,
  );
  const { selectedId } = useParams<{ selectedId: string }>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [estimationItems, setEstimationItems] = useState<IEstimationItem[]>([]);
  const [estimationDescriptions, setEstimationDescriptions] = useState<IEstimationDescription[]>(
    [],
  );
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [documentInfo, setDocumentInfo] = useState<Document | null>(null);
  const navigate = useNavigate();

  // Fetch data from system overview document
  const fetchEstimationData = useCallback(async () => {
    if (!currentProject) {
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    setIsLoading(true);

    const documentId = selectedId || currentProject?.outputs?.["estimation"]?.id;

    if (!documentId) {
      toast.error(TOAST_MESSAGES.ESTIMATION.NOT_GENERATED);
      setIsLoading(false);
      navigate(`/${organization.id}/project/${currentProject.id}/dashboard`);
      return;
    }

    const documentApi = new DocumentApi();
    const response = await documentApi.get(
      currentUser.organization_id,
      currentProject.id,
      documentId,
    );

    if (response === null) {
      toast.error(TOAST_MESSAGES.ESTIMATION.NOT_FOUND);
      setIsLoading(false);
      return;
    }

    setDocumentInfo(response);

    let parsedData: unknown;
    try {
      parsedData = JSON.parse(response.content);
    } catch (error) {
      console.error("Failed to parse estimation content:", error);
      toast.error(TOAST_MESSAGES.ESTIMATION.LOAD_FAILED);
      setEstimationItems([]);
      setEstimationDescriptions([]);
      setIsLoading(false);
      return;
    }

    const usecaseTable = (parsedData as EstimationDocumentContent)?.usecase_table;

    if (usecaseTable && Array.isArray(usecaseTable.headers) && Array.isArray(usecaseTable.rows)) {
      const formattedUsecaseData = formatTableData(usecaseTable.headers, usecaseTable.rows);

      const nextEstimationItems: IEstimationItem[] = [];
      const nextEstimationDescriptions: IEstimationDescription[] = [];

      formattedUsecaseData.forEach((item) => {
        const workloadSource = item["開発コスト"];
        const workloadValue =
          typeof workloadSource === "string"
            ? workloadSource.trim()
            : String(workloadSource ?? "").trim();
        const normalizedWorkload = workloadValue || "0";
        const complexity = normalizedWorkload;

        nextEstimationItems.push({
          selected: typeof item["selected"] === "boolean" ? item["selected"] : true,
          useCase: item["ユースケース"] || "",
          featureCategory: item["ユースケース群"] || "基本機能",
          implementationMethod: item["実装方法"] || "",
          workload: normalizedWorkload,
          complexity: complexity,
        });

        nextEstimationDescriptions.push({
          useCase: item["ユースケース"] || "",
          description: {
            details: item["工数詳細"] || "記載なし",
            reason: item["工数算出理由"] || "記載なし",
          },
        });
      });

      nextEstimationItems.push({
        selected: true,
        useCase: "要件定義・設計",
        featureCategory: "ー",
        implementationMethod: "ー",
        workload: "0.8",
        complexity: "",
      });

      setEstimationItems(nextEstimationItems);
      setEstimationDescriptions(nextEstimationDescriptions);
    } else {
      setEstimationItems([]);
      setEstimationDescriptions([]);
    }

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formatTableData is a pure function defined below
  }, [currentProject, currentUser, organization?.id, navigate, selectedId]);

  useEffect(() => {
    fetchEstimationData();
  }, [fetchEstimationData]);

  // Helper function to format table data
  const formatTableData = (headers: string[], rows: (string[] | UseCaseRow)[]) => {
    return rows.map((row, rowIndex) => {
      if (Array.isArray(row)) {
        return row.reduce(
          (acc, value, index) => {
            const header = headers[index];
            if (header) {
              acc[header] = value;
            }
            return acc;
          },
          { id: `row-${rowIndex}`, selected: true } as Record<string, any>,
        );
      } else {
        return { ...row, selected: row.selected !== undefined ? row.selected : true };
      }
    });
  };

  // Function to toggle item selection
  const toggleItemSelection = (rowIndex: number) => {
    const targetData = [...estimationItems];

    // Toggle the selected state
    const currentItem = targetData[rowIndex];
    if (currentItem) {
      // フロントで追加した「要件定義・設計」は選択不可
      if (currentItem.useCase === "要件定義・設計") return;
      targetData[rowIndex] = {
        selected: !currentItem.selected,
        useCase: currentItem.useCase,
        featureCategory: currentItem.featureCategory,
        implementationMethod: currentItem.implementationMethod,
        workload: currentItem.workload,
        complexity: currentItem.complexity || "",
      };
    }

    setEstimationItems(targetData);
  };

  const handleUpdateTotalWorkLoad = async () => {
    setIsLoading(true);
    try {
      const documentApi = new DocumentApi();
      const estimationDocumentId = currentProject?.outputs?.["estimation"]?.id;

      if (!estimationDocumentId) {
        toast.error(TOAST_MESSAGES.ESTIMATION.NOT_FOUND);
        setIsLoading(false);
        return;
      }

      const response = await documentApi.get(
        currentUser.organization_id,
        currentProject.id,
        estimationDocumentId,
      );

      if (!response) {
        toast.error(TOAST_MESSAGES.ESTIMATION.FETCH_DOCUMENT_FAILED);
        setIsLoading(false);
        return;
      }

      const parsedData = JSON.parse(response.content) as EstimationDocumentContent;
      const usecaseTable = parsedData.usecase_table;

      if (usecaseTable?.rows) {
        const updatedRows = usecaseTable.rows.map((row: string[] | UseCaseRow, index: number) => {
          const estimationItem = estimationItems[index];
          if (estimationItem && estimationItem.useCase !== "要件定義・設計") {
            if (Array.isArray(row)) {
              const rowObj: UseCaseRow = { selected: estimationItem.selected };
              usecaseTable.headers?.forEach((header: string, headerIndex: number) => {
                rowObj[header] = row[headerIndex] || "";
              });
              return rowObj;
            } else {
              return { ...row, selected: estimationItem.selected };
            }
          }
          return row;
        });

        const updatedContent = {
          ...parsedData,
          usecase_table: {
            ...usecaseTable,
            rows: updatedRows,
          },
        };

        const updateResponse = await documentApi.updateContent(
          currentUser.organization_id,
          currentProject.id,
          estimationDocumentId,
          { content: JSON.stringify(updatedContent) },
        );

        if (updateResponse) {
          toast.success(TOAST_MESSAGES.ESTIMATION.CONFIRM_SUCCESS);
        } else {
          toast.error(TOAST_MESSAGES.ESTIMATION.UPDATE_SELECTION_FAILED);
        }
      }
    } catch (error) {
      console.error("Error updating selections:", error);
      toast.error(TOAST_MESSAGES.ESTIMATION.UPDATE_ERROR);
    }

    setIsLoading(false);
  };

  // Columns for the estimation table
  const columns: Array<ColumnDef<IEstimationItem>> = [
    {
      id: "selection",
      header: "",
      cell: ({ row }) => {
        const item = row.original as IEstimationItem;
        if (item.useCase === "要件定義・設計") return null;
        return (
          <input
            type="checkbox"
            checked={item.selected}
            onChange={() => toggleItemSelection(row.index)}
          />
        );
      },
    },
    {
      header: "内訳",
      accessorKey: "useCase",
    },
    {
      header: "要求機能",
      accessorKey: "featureCategory",
    },
    {
      header: "実装方法",
      accessorKey: "implementationMethod",
    },
    {
      header: "工数",
      accessorKey: "workload",
    },
  ];

  // 見積もり概要の計算結果を表示するための変数
  const selectedItems = estimationItems.filter((item) => item.selected);
  const selectedCount = selectedItems.length;

  const totalWorkload = selectedItems.reduce((sum, item) => {
    const parsed = parseFloat(item.workload);
    //  const roundedValue = Math.round(value * 100) / 100;
    return sum + (Number.isFinite(parsed) ? parsed : 0);
  }, 0);

  // Get unique app categories (featureCategory)
  const uniqueAppCategories = new Set<string>();
  selectedItems.forEach((item) => uniqueAppCategories.add(item.featureCategory));
  const uniqueAppCount = uniqueAppCategories.size;

  // Count plugins and customizations
  const pluginCount = selectedItems.filter((item) =>
    item.implementationMethod.includes("プラグイン"),
  ).length;
  const customCount = selectedItems.filter((item) =>
    item.implementationMethod.includes("カスタマイズ"),
  ).length;

  const getExportData = () => {
    const headers = ["No", "ユースケース", "要求機能", "実装方法", "詳細", "見積もり理由", "工数"];
    const rows = selectedItems.map((item, index) => {
      // 対応する詳細と見積もり理由を検索
      const descriptionItem = estimationDescriptions.find((desc) => desc.useCase === item.useCase);
      const details = descriptionItem?.description?.details || "";
      const reason = descriptionItem?.description?.reason || "";

      return [
        `${index + 1}`, // No
        item.useCase, // 内訳(ユースケース名)
        item.featureCategory, // アプリ名
        item.implementationMethod, // 実装方法
        details, // 詳細
        reason, // 見積もり理由
        item.workload.toString(), // 工数
      ];
    });

    return {
      headers,
      rows,
      name: "見積書（初期費用）",
    };
  };

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  return (
    <div className={styles["estimation"]}>
      <CommonDocumentHeader
        title="概算工数見積もり"
        documentType="estimation"
        createdAt={documentInfo?.created_at || ""}
        modifiedAt={documentInfo?.modified_at || ""}
        {...(documentInfo?.modified_by && { modifiedBy: documentInfo.modified_by })}
        isEditable={false}
        isLoading={isLoading}
        handleHistoryClick={() => {
          const pathSegments = window.location.pathname.split("/");
          pathSegments.pop();
          navigate(`${pathSegments.join("/")}/history`);
        }}
        handleExportClick={handleExportClick}
        showEditButton={false}
        showExportButton={true}
      />
      {showExportModal && (
        <ExportModal
          documentType="estimation"
          onClose={() => setShowExportModal(false)}
          exportData={{
            type: "table",
            exportData: getExportData(),
            name: "estimation",
          }}
        />
      )}
      {isLoading ? (
        <div className={styles["loading"]}>読み込み中...</div>
      ) : (
        <>
          {/* 見積もり確定ボタン */}
          <div className={styles["header__buttons"]}>
            <Button
              onClick={() => handleUpdateTotalWorkLoad()}
              styleType="blue"
              label="見積もり確定"
            />
          </div>

          <div className={styles["summaryCard"]}>
            <div className={styles["summaryTitle"]}>工数見積もり ({selectedCount}機能選択中)</div>
            <div className={styles["summaryContent"]}>
              <div className={styles["summaryMetrics"]}>
                <div className={styles["summaryItem"]}>
                  <span>合計工数:</span>
                  <span>{totalWorkload.toFixed(2)} 人月</span>
                </div>
                <div className={styles["summaryItem"]}>
                  <span>要求機能数:</span>
                  <span>{uniqueAppCount - 1}</span>
                </div>
                <div className={styles["summaryItem"]}>
                  <span>プラグイン数:</span>
                  <span>{pluginCount}</span>
                </div>
                <div className={styles["summaryItem"]}>
                  <span>カスタマイズ数:</span>
                  <span>{customCount}</span>
                </div>
              </div>
            </div>
          </div>
          <p className={styles["estimation__note"]}>
            ※見積もり確定後、費用対効果ページ上でROIグラフを編集することができます。
          </p>

          <EstimationTable data={estimationItems} columns={columns} />

          <h2>根拠説明</h2>
          <EstimationDescription descriptions={estimationDescriptions} />
        </>
      )}
    </div>
  );
};

export default EstimationPage;
